
import React, { useEffect } from 'react';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAuthModal: (mode: 'login' | 'signup') => void;
}

const MobileNavLink: React.FC<{ href: string; children: React.ReactNode; onClick: () => void; }> = ({ href, children, onClick }) => (
    <a href={href} onClick={onClick} className="block py-4 text-lg font-bold text-gray-800 hover:text-[#003B5C] transition-colors border-b border-gray-50 last:border-0">
        {children}
    </a>
);

interface SocialIconProps {
  href: string;
  children: React.ReactNode;
  'aria-label': string;
}

const SocialIcon: React.FC<SocialIconProps> = ({ href, children, 'aria-label': ariaLabel }) => (
  <a 
    href={href} 
    target="_blank" 
    rel="noopener noreferrer" 
    aria-label={ariaLabel} 
    className="text-gray-500 hover:text-[#003B5C] transition-colors duration-300"
  >
    {children}
  </a>
);

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, onOpenAuthModal }) => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    const handleLoginClick = () => {
        onClose();
        onOpenAuthModal('login');
    };

    const handleSignupClick = () => {
        onClose();
        onOpenAuthModal('signup');
    };

    return (
        <div 
            className={`fixed inset-0 z-[60] transition-opacity duration-300 lg:hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            aria-hidden={!isOpen}
            role="dialog"
            aria-modal="true"
        >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
            
            {/* Menu Panel */}
            <nav className={`mobile-menu absolute top-0 right-0 h-full w-full max-w-sm bg-white shadow-xl flex flex-col p-6 ${isOpen ? 'is-open' : ''}`}>
                <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
                     <div className="flex items-center space-x-2">
                        <img src="https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/icons/OVL+-+NewLogo.webp" alt="Omniviewlabs Logo" className="h-10" />
                        <span className="text-xl font-bold tracking-wider" style={{ color: '#003B5C' }}>Omniviewlabs</span>
                    </div>
                    <button onClick={onClose} className="p-2 -mr-2" aria-label="Close menu">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex flex-col">
                    <MobileNavLink href="#about" onClick={onClose}>About Us</MobileNavLink>
                    <MobileNavLink href="#products" onClick={onClose}>Solutions</MobileNavLink>
                    <MobileNavLink href="#other-features" onClick={onClose}>Features</MobileNavLink>
                    <MobileNavLink href="#case-study" onClick={onClose}>Case Studies</MobileNavLink>
                    <MobileNavLink href="#blog" onClick={onClose}>Insights</MobileNavLink>
                </div>
                
                <div className="mt-auto">
                    <div className="pt-6 border-t border-gray-200 space-y-4 mb-8">
                        <button
                            onClick={handleLoginClick}
                            className="w-full text-center font-black uppercase tracking-widest text-xs transition-colors duration-300 px-4 py-4 rounded-2xl text-gray-700 bg-gray-100 hover:bg-gray-200"
                        >
                            Login
                        </button>
                        <button
                            onClick={handleSignupClick}
                            className="w-full text-center bg-[#003B5C] hover:bg-[#002840] text-white font-black uppercase tracking-widest text-xs py-4 px-5 rounded-2xl transition-all duration-300"
                        >
                            Join Us
                        </button>
                    </div>

                    <div className="text-center">
                        <h3 className="text-[10px] font-black text-gray-400 mb-4 tracking-[0.2em] uppercase">Connect With Us</h3>
                        <div className="flex justify-center space-x-6">
                            <SocialIcon href="https://www.facebook.com/omniview.labs.official" aria-label="Facebook">
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" /></svg>
                            </SocialIcon>
                            <SocialIcon href="https://www.instagram.com/omniview_labs" aria-label="Instagram">
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                            </SocialIcon>
                            <SocialIcon href="https://www.linkedin.com/company/omniviewlabs" aria-label="LinkedIn">
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" /></svg>
                            </SocialIcon>
                        </div>
                    </div>
                </div>
            </nav>
        </div>
    );
};

export default MobileMenu;
